import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import modusLogo from "../Modus_logo.png";

const LOGIN_URL = "https://pdm.modus.org";

/**
 * Shared across every marketing page. `active` marks which nav item
 * gets the "active" highlight — matches the pattern each of the
 * original static HTML pages used (an `active` class on the current
 * page's own link). Links are real routes now, not same-page scroll
 * anchors, since each of these became its own actual page.
 *
 * Below the 980px breakpoint, .nav-links used to just disappear
 * (display:none) with nothing replacing it — visitors on a tablet or
 * narrower lost access to the entire nav. This adds a real hamburger
 * toggle that reveals the same links in a dropdown instead of hiding
 * them outright.
 */
export default function SiteHeader({ active }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on every navigation, so it never stays open
  // pointing at a page the visitor already left.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navItem = (to, label, key) => (
    <Link to={to} className={active === key ? "active" : ""}>
      {label}
    </Link>
  );

  return (
    <header id="siteHeader" className={scrolled ? "scrolled" : ""}>
      <div className="wrap nav">
        <Link className="logo" to="/">
          <img src={modusLogo} alt="Modus" className="logo-img" />
        </Link>
        <nav className="nav-links">
          {navItem("/platform", "Platform", "platform")}
          {navItem("/who-we-help", "Who We Help", "who-we-help")}
          {navItem("/why-modus", "Why Modus", "why-modus")}
          {navItem("/resources", "Resources", "resources")}
          {navItem("/about", "About", "about")}
        </nav>
        <div className="nav-cta">
          <a className="login-link" href={LOGIN_URL} target="_blank" rel="noopener noreferrer">
            Login
          </a>
          <button className="btn btn-teal" onClick={() => (window.location.href = "/about#contact")}>
            Book a Demo
          </button>
        </div>
        <button
          className="menu-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          {menuOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          )}
        </button>
      </div>

      <nav className={`mobile-nav ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="mobile-nav-inner">
          {navItem("/platform", "Platform", "platform")}
          {navItem("/who-we-help", "Who We Help", "who-we-help")}
          {navItem("/why-modus", "Why Modus", "why-modus")}
          {navItem("/resources", "Resources", "resources")}
          {navItem("/about", "About", "about")}
          <div className="mobile-nav-cta">
            <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer">
              Login
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}